import * as React from 'react';

/**
 * Adds LabSmarts guidance to a stock Amplify v3 auth screen without restating
 * its render.
 *
 * The base `showComponent` produces `<FormSection>` wrapping a header, a body
 * and a footer. This locates the body by its `data-test` attribute, hands its
 * children to `addNotes`, and clones the tree back together.
 *
 * If the tree does not look the way we expect — a different Amplify build, a
 * body that moved — the original screen is returned untouched. A missing hint
 * is a far better outcome than an auth screen that fails to render.
 */
export function withSectionBodyNotes(
  section: React.ReactNode,
  bodyDataTest: string,
  addNotes: (bodyChildren: React.ReactNode[]) => React.ReactNode[] | null,
): React.ReactNode {
  const element = section as React.ReactElement<any>;
  if (!element || !element.props) {
    return section;
  }

  const parts = React.Children.toArray(element.props.children) as React.ReactElement<any>[];
  const bodyIndex = parts.findIndex(
    part => part && part.props && part.props['data-test'] === bodyDataTest,
  );
  if (bodyIndex < 0) {
    return section;
  }

  const body = parts[bodyIndex];
  const annotated = addNotes(React.Children.toArray(body.props.children));
  if (!annotated) {
    return section;
  }

  parts[bodyIndex] = React.cloneElement(body, body.props, annotated);
  return React.cloneElement(element, element.props, parts);
}
